import { useCallback, useEffect } from 'react';
import { AppDispatch } from '../../../app/store';
import { clearScaleMetadata, clearTrackData, clearTrackDataCache } from '../templateSlice';

interface UseTemplateLogViewerParams {
  templateIdParam?: string | null;
  activeTemplateId?: string | null;
  isLocalTemplate: boolean;
  clientId?: string;
  projectId?: string;
  boreholeId?: string;
  selectedViewId?: string | null;
  dispatch: AppDispatch;
  history: { push: (path: string) => void };
  setActiveTemplateId: (value: string | null) => void;
  setIsLocalTemplate: (value: boolean) => void;
  setSelectedViewId: (value: string | null) => void;
  setSelectedBoreholeIds: (value: number[]) => void;
  setAddedBoreholeIds: (value: number[]) => void;
  setPendingBoreholeIds: (value: number[]) => void;
  setHasConfirmedBoreholes: (value: boolean) => void;
  setNoDataTrackTypeIds: (value: number[]) => void;
  setAddedTrackTypeIds: (value: number[]) => void;
  setLastTrackRequest: (value: any) => void;
  setAddTrackTargetBoreholeId: (value: string) => void;
}

const useTemplateLogViewer = ({
  templateIdParam,
  activeTemplateId,
  isLocalTemplate,
  clientId,
  projectId,
  boreholeId,
  selectedViewId,
  dispatch,
  history,
  setActiveTemplateId,
  setIsLocalTemplate,
  setSelectedViewId,
  setSelectedBoreholeIds,
  setAddedBoreholeIds,
  setPendingBoreholeIds,
  setHasConfirmedBoreholes,
  setNoDataTrackTypeIds,
  setAddedTrackTypeIds,
  setLastTrackRequest,
  setAddTrackTargetBoreholeId
}: UseTemplateLogViewerParams) => {
  useEffect(() => {
    if (!templateIdParam) {
      if (!isLocalTemplate && activeTemplateId) {
        setActiveTemplateId(null);
      }
      return;
    }
    if (templateIdParam === activeTemplateId) return;
    setActiveTemplateId(templateIdParam);
  }, [templateIdParam, activeTemplateId, isLocalTemplate, setActiveTemplateId]);

  const onTemplateSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextId = e.target.value || null;
    if (!nextId || nextId === activeTemplateId) return;
    if (selectedViewId) {
      setSelectedViewId(null);
      setSelectedBoreholeIds([]);
      setAddedBoreholeIds([]);
      setPendingBoreholeIds([]);
      setHasConfirmedBoreholes(false);
    }
    setLastTrackRequest(null);
    dispatch(clearTrackData());
    dispatch(clearTrackDataCache());
    dispatch(clearScaleMetadata());
    setNoDataTrackTypeIds([]);
    setAddedTrackTypeIds([]);
    setIsLocalTemplate(false);
    setActiveTemplateId(nextId);
    setAddTrackTargetBoreholeId('all');
    if (clientId && projectId && boreholeId) {
      history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/${nextId}`);
    }
  }, [
    activeTemplateId,
    selectedViewId,
    setSelectedViewId,
    setSelectedBoreholeIds,
    setAddedBoreholeIds,
    setPendingBoreholeIds,
    setHasConfirmedBoreholes,
    setLastTrackRequest,
    dispatch,
    setNoDataTrackTypeIds,
    setAddedTrackTypeIds,
    setIsLocalTemplate,
    setActiveTemplateId,
    setAddTrackTargetBoreholeId,
    clientId,
    projectId,
    boreholeId,
    history
  ]);

  return {
    onTemplateSelectChange
  };
};

export default useTemplateLogViewer;
